import { useState, type FormEvent } from 'react';

const FORM_ACTION = 'https://readdy.ai/api/form/db92u9s6oi3g3tv8us1g';

const feedbackTypes = ['内容纠错', '作品推荐', '合作咨询', '使用问题', '其他建议'];

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFeedback('提交成功，感谢你的反馈，我们会尽快查看！');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setFeedback('');

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        setFeedback('提交成功，感谢你的反馈，我们会尽快查看！');
        form.reset();
      } else {
        setStatus('error');
        setFeedback(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFeedback('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      data-readdy-form
      id="hongxing-feedback-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="请输入你的邮箱"
            className="h-11 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="contact-type" className="text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="contact-type"
          name="topic"
          required
          defaultValue=""
          className="h-11 cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        >
          <option value="" disabled>
            请选择反馈类型
          </option>
          {feedbackTypes.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你想反馈的内容（最多 500 字）"
          className="resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-opt-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status !== 'idle' && status !== 'submitting' && (
        <div
          className={`flex items-start gap-2 rounded-md px-4 py-3 text-sm ${
            status === 'success'
              ? 'bg-accent-50 text-accent-800'
              : 'bg-primary-50 text-primary-800'
          }`}
        >
          <i className={`${status === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mt-0.5`} />
          <span>{feedback}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}